import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { Box, CardContent, Divider, Grid, Stack, Typography } from '@mui/material';
import ButtonColor from 'src/components/Forms/Buttons';
import { yupResolver } from '@hookform/resolvers/yup';

import TextFieldFormHook from 'src/components/Forms/Inputs';
import useLanguage from 'src/hooks/Language/useLanguage';
import SelectFieldFormHook from 'src/components/Forms/Select';
import { TProductDto, ProductStatus } from 'src/types/Products/product.model';
import { useCreateProduct } from 'src/hooks/Products/useCreateProduct';
export interface CreateProductProps {
  handleCreatedSuccess: (id: number, isSuccess: boolean) => void;
}

export default function CreateProduct({ handleCreatedSuccess }: CreateProductProps) {
  const {
    language: { product, common }
  } = useLanguage();

  const { productInfo, categories, units, productStatus, branchs, mutationCreateProduct, mutationUpdateProduct } =
    useCreateProduct(handleCreatedSuccess);
  const validationSchema: Yup.SchemaOf<TProductDto> = Yup.object().shape({
    name: Yup.string().required(),
    code: Yup.string().required(),
    unitId: Yup.number().required(),
    categoryId: Yup.number().required(),
    branchId: Yup.number().required(),
    salePrice: Yup.number().required(),
    importPrice: Yup.number().required(),
    width: Yup.number().required(),
    height: Yup.number().required(),
    length: Yup.number().required(),
    weight: Yup.number().required(),
    status: Yup.number().required(),
    description: Yup.string().notRequired(),
    id: Yup.number().notRequired()
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<TProductDto>({
    mode: 'onSubmit',
    defaultValues: {},
    resolver: yupResolver(validationSchema)
  });

  useEffect(() => {
    if (productInfo) reset(productInfo?.data);
  }, [productInfo?.data]);

  const onSubmit = (data: TProductDto) => {
    data.id ? mutationCreateProduct.mutateAsync(data) : mutationUpdateProduct.mutateAsync(data);
  };

  return (
    <form className="form">
      <Box p={3} display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h4" gutterBottom>
            {product.titleHeader}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <CardContent sx={{ p: 4 }}>
        <Typography variant="subtitle2">
          <Box>
            <Typography className="my-12" variant="body1" fontWeight="bold" gutterBottom noWrap>
              Thông tin sản phẩm
            </Typography>

            <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
              <Grid item xs={4} md={4}>
                <TextFieldFormHook
                  register={register}
                  id="code"
                  name="code"
                  variant="outlined"
                  errorMessage={errors.code?.message}
                  label="Mã Sản phẩm"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={8} md={8}>
                <TextFieldFormHook
                  register={register}
                  id="name"
                  name="name"
                  variant="outlined"
                  errorMessage={errors.name?.message}
                  label="Tên Sản phẩm"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <SelectFieldFormHook
                  register={register}
                  id="categoryId"
                  name="roleFId"
                  label="Danh mục"
                  variant="outlined"
                  dataOptions={categories}
                  errorMessage={errors.categoryId?.message}
                  required
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <SelectFieldFormHook
                  register={register}
                  id="unitId"
                  name="unitId"
                  label="Đơn vị tính"
                  variant="outlined"
                  dataOptions={units}
                  errorMessage={errors.unitId?.message}
                  required
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextFieldFormHook
                  type="textarea"
                  register={register}
                  id="description"
                  name="description"
                  variant="outlined"
                  errorMessage={errors.description?.message}
                  label="Mô tả sản phẩm"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <SelectFieldFormHook
                  register={register}
                  id="branchId"
                  name="branchId"
                  label="Thương hiệu"
                  variant="outlined"
                  dataOptions={branchs}
                  errorMessage={errors.branchId?.message}
                  required
                />
              </Grid>
            </Grid>
            <Typography className="my-12" variant="body1" fontWeight="bold" gutterBottom noWrap>
              Thông tin giá
            </Typography>
            <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
              <Grid item xs={6} md={6}>
                <TextFieldFormHook
                  register={register}
                  id="price"
                  name="importPrice"
                  variant="outlined"
                  errorMessage={errors.importPrice?.message}
                  label="Giá nhập"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <TextFieldFormHook
                  register={register}
                  id="salePrice"
                  name="salePrice"
                  variant="outlined"
                  errorMessage={errors.salePrice?.message}
                  label="Giá Bán"
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Typography className="my-12" variant="body1" fontWeight="bold" gutterBottom noWrap>
              Thông tin vận chuyển
            </Typography>
            <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
              <Grid item xs={6} md={6}>
                <TextFieldFormHook
                  register={register}
                  id="width"
                  name="width"
                  variant="outlined"
                  errorMessage={errors.width?.message}
                  label="Dài"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <TextFieldFormHook
                  register={register}
                  id="length"
                  name="length"
                  variant="outlined"
                  errorMessage={errors.length?.message}
                  label="Rộng"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <TextFieldFormHook
                  register={register}
                  id="height"
                  name="height"
                  variant="outlined"
                  errorMessage={errors.height?.message}
                  label="Cao"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <TextFieldFormHook
                  register={register}
                  id="weight"
                  name="weight"
                  variant="outlined"
                  errorMessage={errors.weight?.message}
                  label="Trọng lượng"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <SelectFieldFormHook
                  register={register}
                  id="status"
                  name="status"
                  label="Trạng thái"
                  variant="outlined"
                  dataOptions={productStatus}
                  defaultValue={ProductStatus.Active}
                  errorMessage={errors.status?.message}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        </Typography>
      </CardContent>

      <Divider />
      <CardContent className="float-right">
        <Grid item xs={12} sm={5} className="flex justify-start">
          <Stack direction="row" spacing={2} className="h-24 self-right">
            <ButtonColor
              //  isFetchingData={data?.userId ? mutationUpdateProfile.isLoading : mutationPostCreateUser.isLoading}
              type="submit"
              className="px-16"
              color="success"
              onClick={handleSubmit(onSubmit)}
              btnTitle={common.buttons.submit}
            ></ButtonColor>
          </Stack>
        </Grid>
      </CardContent>
    </form>
  );
}
